<template>
  <v-dialog
    v-model="dialog"
    persistent
    scrollable
    :fullscreen="$vuetify.display.xs"
    :width="$vuetify.display.smAndUp ? 600 : 'auto'"
  >
    <template v-slot:activator="{ props }">
      <div v-bind="props" data-cy="dialog-deleteNode-activator">
        <slot></slot>
      </div>
    </template>

    <div class="vb-dialog vb-dialog-danger">
      <v-card v-if="waitingForUsersCount">
        <v-card-title class="d-flex justify-space-between">
          <span>
            {{ $t('routes.node.deleteNode') }}
          </span>

          <v-btn icon="mdi-close" @click="dialog = false" :aria-label="$t('common.close')" />
        </v-card-title>

        <v-card-text class="pt-5 scrollable">
          <div v-if="errorMessage" class="bg-error">
            {{ errorMessage }}
          </div>
          <div v-if="errorTranslatable" class="bg-error">
            {{ $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content) }}
          </div>
          <div v-else class="text-center">
            <v-progress-circular
              class="float-left"
              indeterminate
              color="primary"
              :aria-label="$t('routes.node.loading')"
            >
            </v-progress-circular>
            <h3 class="pl-2 mt-2 float-left">{{ $t('routes.node.loading') }}</h3>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" @click="cancel" data-cy="dialog-deleteNode-false">{{ $t('common.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>

      <v-card v-if="!waitingForUsersCount" data-cy="dialog-deleteNode-modal">
        <v-card-title class="bg-red-darken-3 d-flex justify-space-between">
          <span>{{ $t('routes.node.deleteNode') }}</span>

          <v-btn icon="mdi-close" @click="dialog = false" :aria-label="$t('common.close')" />
        </v-card-title>

        <v-card-text class="pt-5 scrollable" data-cy="dialog-deleteNode-nodeName">
          <template v-if="affectedFtpUsers.length > 0">
            <div class="tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ $t('routes.node.deleteFtpUsersWarning') }}</span>
              <ul class="mx-5 mt-1">
                <li v-for="group in affectedFtpUsersByNode" :key="group.nodeId">
                  <router-link :to="'/node/' + group.nodeId">{{ group.nodeName }}</router-link>
                  ({{ group.usernames.join(', ') }})
                </li>
              </ul>
            </div>
          </template>
          <template v-else>
            <div>{{ $t('routes.node.deleteNodeDialogMessage') }}</div>
            <div class="py-4">
              <v-icon>mdi-folder</v-icon>
              {{ node.name }}
            </div>

            <div>
              <v-checkbox
                v-model="deletePermanently"
                density="compact"
                hide-details
                :label="$t('routes.node.deleteDialogDeletePermanently')"
              />
              <div v-if="deletePermanently">
                <div class="mt-2 tw-warning">
                  <v-icon>mdi-alert-box</v-icon>
                  <span>{{ $t('routes.node.deleteNodeDialogDeletePermanentlyWarning') }}</span>
                </div>
                <div class="mt-2 tw-warning">
                  <v-icon>mdi-alert-box</v-icon>
                  <span>{{ $t('routes.node.deletePermanentlyNodeRecycleBinWarning') }}</span>
                </div>
              </div>
            </div>

            <div v-if="userCount === 0" class="mt-2 tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ $t('routes.node.deleteNodeDialogMessageNoUsers') }}</span>
            </div>
            <div v-else class="mt-2 tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ $t('routes.node.deleteNodeDialogMessageWithUsers', userCount) }}</span>
            </div>
            <div class="mt-2 tw-warning">
              <v-icon>mdi-alert-box</v-icon>
              <span>{{ $t('routes.node.deleteDialogArchivedFilesCannotBeRestored') }}</span>
            </div>

            <div v-if="errorMessage && errorCode !== 'ERR233'" class="bg-error">
              {{ errorMessage }}
            </div>
            <div v-if="errorCode === 'ERR233' && errorItems" class="bg-error my-2">
              {{ $t('backend.ERR233', errorParams) }}
              <span v-if="errorItems.length === 1">
                {{ $t('routes.node.depthExceededTooDeep', errorItems[0].children.length) }}
                <DepthExceededChildren :children="errorItems[0].children" @navigate="dialog = false" />
              </span>
              <template v-else>
                {{ $t('routes.node.depthExceededTooDeep', 2) }}
                <ul class="mx-5 mt-1">
                  <li v-for="item in errorItems" :key="item.parent.id">
                    <strong>{{ item.parent.name }}</strong> &gt;
                    <DepthExceededChildren :children="item.children" @navigate="dialog = false" />
                  </li>
                </ul>
              </template>
            </div>
            <div v-if="errorTranslatable && errorCode !== 'ERR233'" class="bg-error">
              {{
                $replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content)
              }}
            </div>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            v-if="affectedFtpUsers.length === 0"
            @click="deleteNode"
            variant="flat"
            elevation="0"
            color="red-darken-3"
            data-cy="dialog-deleteNode-ok"
            :disabled="deletionInProgress"
          >
            {{ $t('common.delete') }}
          </v-btn>
          <v-btn variant="text" @click="cancel" data-cy="dialog-deleteNode-false">{{ $t('common.cancel') }}</v-btn>
        </v-card-actions>
      </v-card>
    </div>
  </v-dialog>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js';
import { useNodeStore } from '@/stores/NodeStore.js';
import DialogActionKeys from '@/components/mixin/DialogActionKeys.js';
import DepthExceededChildren from '@/components/DepthExceededChildren.vue';

export default {
  mixins: [DialogActionKeys],
  components: { DepthExceededChildren },
  name: 'DeleteNodeDialog',
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
    node: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      waitingForUsersCount: true,
      userCount: 0,
      errorMessage: '',
      errorTranslatable: '',
      errorCode: null,
      errorParams: {},
      errorItems: null,
      deletionInProgress: false,
      deletePermanently: false,
      affectedFtpUsers: [],
    };
  },
  methods: {
    getRedirectToNodeId() {
      let redirectToNode = null;

      if (this.currentNode.id === this.node.id) {
        if (this.currentNode.parentid) {
          redirectToNode = this.currentNode.parentid;
        } else {
          const authStore = useAuthStore();
          redirectToNode = authStore.rootNodeId;
        }

        return redirectToNode;
      }

      const ids = this.currentNode.rootpath.split('/').filter((id) => id !== '');

      if (ids.includes(this.node.id)) {
        if (this.node.parentid) {
          redirectToNode = this.node.parentid;
        } else {
          const authStore = useAuthStore();
          redirectToNode = authStore.rootNodeId;
        }
      }

      return redirectToNode;
    },
    async deleteNode() {
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.deletionInProgress = true;

      const redirectToNodeId = this.getRedirectToNodeId(this.node);

      const nodeStore = useNodeStore();

      try {
        await nodeStore.deleteNodes([this.node.id], this.deletePermanently);

        if (redirectToNodeId !== null) {
          setTimeout(() => {
            this.$router.push({ path: `/node/${redirectToNodeId}` });
          }, 500);
        }

        this.dialog = false;
        this.userCount = 0;
      } catch (error) {
        if (error?.response?.data?.error) {
          const apiError = error.response.data.error;
          if (apiError?.code === 'ERR233') {
            this.errorCode = apiError.code;
            this.errorParams = { ...apiError.params };
            if (apiError.extra?.items) {
              this.errorItems = apiError.extra.items;
            }
          } else if (apiError?.code && apiError?.params) {
            this.errorTranslatable = apiError;
          } else {
            this.errorMessage = apiError?.message || this.$t('routes.node.deleteNodeDialogErrorDeletingActiveNode');
          }
        } else {
          if (error?.name === 'AxiosError') {
            console.warn(`AxiosError: ${error?.message}`);
          } else {
            // unhandled error
            this.errorMessage = this.$t('routes.node.deleteNodeDialogErrorDeletingActiveNode');
            console.log('Error deleting active node: ', error);
          }
        }
      } finally {
        this.deletionInProgress = false;
      }
    },
    cancel() {
      this.dialog = false;
      this.waitingForUsersCount = false;
      this.userCount = 0;
      this.affectedFtpUsers = [];
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.errorCode = null;
      this.errorParams = {};
      this.errorCode = null;
      this.errorParams = {};
      this.errorItems = null;
    },
  },
  computed: {
    dialog: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    currentNode() {
      const nodeStore = useNodeStore();
      return nodeStore.getCurrentNode;
    },
    affectedFtpUsersByNode() {
      const groups = {};
      for (const user of this.affectedFtpUsers) {
        if (!groups[user.nodeId]) {
          groups[user.nodeId] = { nodeId: user.nodeId, nodeName: user.nodeName, usernames: [] };
        }
        groups[user.nodeId].usernames.push(user.username);
      }
      return Object.values(groups);
    },
  },
  watch: {
    async dialog(newValue) {
      this.errorMessage = '';
      this.errorTranslatable = '';
      this.deletePermanently = false;
      this.affectedFtpUsers = [];

      if (newValue) {
        this.deletionInProgress = false;
        this.waitingForUsersCount = true;
        const nodeStore = useNodeStore();

        try {
          const [usersResult, ftpUsers] = await Promise.all([
            nodeStore.getUsersForNodes([this.node.id]),
            nodeStore.getAffectedFtpUsers([this.node.id]),
          ]);
          this.userCount = usersResult.enabled;
          this.affectedFtpUsers = ftpUsers || [];
          this.waitingForUsersCount = false;
        } catch (error) {
          console.log('Error getting user count: ', error);

          if (error?.response?.data?.error) {
            const apiError = error.response.data.error.message;
            if (typeof apiError === 'object') {
              this.errorTranslatable = apiError;
            } else {
              this.errorMessage = apiError;
            }
          } else {
            this.errorMessage = this.$t('routes.node.deleteNodeDialogErrorDeletingActiveNode');
          }
        }
      }
    },
  },
};
</script>

<style scoped>
</style>
